/**
 * NotificationsAdmin.jsx — Admin ▸ Notifications.
 *
 * Three surfaces:
 *  1. System notifications (catalog) — the built-in flows, surfaced for
 *     visibility. case_submitted recipients are editable; sign-off→requester is
 *     locked (routing is intrinsic).
 *  2. Custom flow rules — LIST + MANAGEMENT only: enable/disable, reorder,
 *     delete, live match preview. Creating/editing a rule happens in the rule
 *     wizard page ('/admin/notifications/rules/new' and
 *     '/admin/notifications/rules/<id>'); this card list never edits rule
 *     internals. Includes the scheduled-sending master switch.
 *  3. Recipient groups — the named staff lists ({ kind:'group' } recipient
 *     specs resolve against them), config key `recipientGroups`.
 *
 * Persists via onPublish(key, value, { publish: true }) — the governed
 * admin_config write (publish gate + audit hash-chain).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RecipientEditor, { recipientSummary, KIND_LABELS } from './RecipientEditor.jsx';
import { MultiValueInput } from './RuleBuilder.jsx';
import { ruleIsPreviewable } from '../utils/ruleFields.js';
import { previewNotificationRule } from '../api/client.js';
import { SYSTEM_NOTIFICATIONS } from '@shared/notificationCatalog.js';
import { normalizeRule, COALESCE_MAX_WINDOW_DEFAULT } from '@shared/notificationSchema.js';

// Re-export so existing/import-by-old-path consumers keep working after the
// RecipientEditor extraction.
export { default as RecipientEditor, recipientSummary, KIND_LABELS, KIND_NEEDS_VALUE } from './RecipientEditor.jsx';

export const TRIGGER_LABELS = {
  case_submitted:         'Case submitted',
  case_updated:           'Case updated',
  status_changed:         'Status changed',
  severity_changed:       'Severity changed',
  attachment_uploaded:    'File uploaded',
  requirement_signed_off: 'Sign-off completed',
  requirement_added:      'Requirement added',
  comment_added:          'Comment added (public)',
  comment_added_private:  'Private comment added',
};

/** The wizard routes this page links to (owned by the rule-wizard page). */
export const RULE_WIZARD_NEW_ROUTE = '/admin/notifications/rules/new';
export const ruleWizardRoute = (id) => `/admin/notifications/rules/${id}`;

// ── One custom rule — a collapsed management card (edit lives in the wizard) ──
function RuleCard({ rule, groups, onChange, onDelete, onEdit, onMoveUp, onMoveDown, isFirst, isLast }) {
  const norm = normalizeRule(rule);
  const isScheduled = norm.kind === 'scheduled';
  const batched = !isScheduled && Number.isInteger(norm.coalesce?.windowMinutes)
    ? ` · batched (${norm.coalesce.windowMinutes}m quiet / ${
        Number.isInteger(norm.coalesce.maxWindowMinutes) ? norm.coalesce.maxWindowMinutes : COALESCE_MAX_WINDOW_DEFAULT}m max)`
    : '';
  const whenLabel = isScheduled
    ? `Scheduled · daily${
        norm.refire === 'rearm_on_activity' ? ' · re-arms on activity'
        : norm.refire === 'every_run'       ? ' · daily report (every scan)'
        : ' · once per case'}${norm.perCase ? ' · one email per case' : ''}${
        Array.isArray(rule.escalation) && rule.escalation.length
          ? ` · escalates at ${rule.escalation.map(st => `${st.afterDays}d`).join(', ')}` : ''}`
    : (norm.triggers.length
        ? norm.triggers.map(t => TRIGGER_LABELS[t] || t).join(' / ') + batched
        : 'No trigger' + batched);

  // Live "which current cases match" preview — on demand (a click), not per
  // render, so a long rule list doesn't fan out scan requests. Uses the
  // notification preview endpoint (understands v1/v2 rules, per-kind ctx).
  const previewable = !!norm.conditions && ruleIsPreviewable(norm.conditions);
  const [preview, setPreview] = useState(null);        // null | 'busy' | { matches, total, capped }
  const [previewError, setPreviewError] = useState(null);
  useEffect(() => {
    // Any change to the rule invalidates a fetched count.
    setPreview(null);
    setPreviewError(null);
  }, [JSON.stringify(rule)]);

  async function checkMatches() {
    setPreview('busy');
    setPreviewError(null);
    try {
      const r = await previewNotificationRule(rule);
      setPreview(r);
    } catch (e) {
      setPreview(null);
      setPreviewError(e?.message || 'Preview failed.');
    }
  }

  return (
    <div className="panel" style={{ marginBottom: 10, overflow: 'visible' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
        <input type="checkbox" checked={rule.enabled !== false} onChange={e => onChange({ ...rule, enabled: e.target.checked })}
          style={{ accentColor: 'var(--brand)', width: 15, height: 15 }} title="Enabled" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{rule.name || 'Untitled rule'}</span>
            {isScheduled && (
              <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>scheduled</span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            On <strong style={{ fontWeight: 600 }}>{whenLabel}</strong> → {recipientSummary(rule.recipients, groups)}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)' }} aria-live="polite">
            {!norm.conditions && 'No conditions — fires on every matching event.'}
            {norm.conditions && !previewable && 'Conditions incomplete — edit the rule to finish them.'}
            {previewable && preview === null && !previewError && (
              <button type="button" className="btn ghost sm"
                      onClick={checkMatches}
                      style={{ fontSize: 11, padding: '1px 6px' }}>
                Check current matches
              </button>
            )}
            {previewable && preview === 'busy' && 'Checking which current cases match…'}
            {previewable && preview && preview !== 'busy' && (
              <>Currently matches <strong style={{ color: 'var(--text-2)', fontWeight: 600 }}>{preview.matches}{preview.capped ? '+' : ''}</strong> of {preview.total} case{preview.total === 1 ? '' : 's'} ({isScheduled ? 'open cases — the daily scan sends per its re-fire mode' : 'existing cases — a guide; the rule only fires on new events'}).</>
            )}
            {previewError && <span style={{ color: 'var(--danger)' }}>{previewError}</span>}
          </div>
        </div>
        <button type="button" className="btn ghost sm" onClick={onMoveUp} disabled={isFirst} title="Move up" style={{ fontSize: 12 }}>↑</button>
        <button type="button" className="btn ghost sm" onClick={onMoveDown} disabled={isLast} title="Move down" style={{ fontSize: 12 }}>↓</button>
        <button type="button" className="btn ghost sm" onClick={onEdit} style={{ fontSize: 12 }}>Edit</button>
        <button type="button" className="btn ghost sm" onClick={onDelete} title="Delete rule"
                style={{ fontSize: 12, color: 'var(--danger)' }}>Delete</button>
      </div>
    </div>
  );
}

// ── Recipient groups — named staff lists for { kind:'group' } specs ──────────
function RecipientGroupsEditor({ groups, onChange }) {
  const list = Array.isArray(groups) ? groups : [];
  const setAt = (i, next) => onChange(list.map((g, j) => (j === i ? next : g)));
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const add = () => onChange([...list, { key: '', label: '', emails: [] }]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {list.map((group, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input type="text" className="input" placeholder="Group name (e.g. Training team)"
              value={group.label || ''}
              onChange={e => setAt(i, { ...group, label: e.target.value })}
              style={{ fontSize: 13, padding: '6px 10px', flex: 1, minWidth: 180 }} maxLength={120} />
            <input type="text" className="input" placeholder="key (e.g. training-team)"
              value={group.key || ''}
              onChange={e => setAt(i, { ...group, key: e.target.value.toLowerCase() })}
              style={{ fontSize: 13, padding: '6px 10px', flex: '0 0 200px', fontFamily: 'var(--mono, monospace)' }}
              maxLength={60} aria-label="Group key" />
            <button type="button" className="btn ghost sm" onClick={() => remove(i)} style={{ fontSize: 12 }}>Remove</button>
          </div>
          <MultiValueInput
            values={Array.isArray(group.emails) ? group.emails : []}
            onChange={emails => setAt(i, { ...group, emails })}
            placeholder="name@therapieclinic.com, other@therapieclinic.com…" />
        </div>
      ))}
      {list.length === 0 && <div className="field-hint">No recipient groups yet.</div>}
      <div>
        <button type="button" className="btn ghost sm" onClick={add} style={{ fontSize: 13 }}>+ Add group</button>
      </div>
    </div>
  );
}

export default function NotificationsAdmin({ config, onPublish }) {
  const navigate = useNavigate();
  const savedRules = Array.isArray(config?.notificationRules) ? config.notificationRules : [];
  const savedSystem = config?.caseSubmittedNotification || { enabled: true, recipients: [] };
  const savedGroups = Array.isArray(config?.recipientGroups) ? config.recipientGroups : [];
  const savedSched = config?.scheduledNotificationsEnabled === true;

  const savedTx = Array.isArray(config?.treatmentSubmitNotify) ? config.treatmentSubmitNotify : [];
  const treatmentOptions = (config?.treatmentTypes || []).map(t => t.value).filter(Boolean);

  const [rules, setRules]   = useState(savedRules);
  const [system, setSystem] = useState(savedSystem);
  const [txNotify, setTxNotify] = useState(savedTx);
  const [groups, setGroups] = useState(savedGroups);
  const [sched, setSched]   = useState(savedSched);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);
  const [notice, setNotice] = useState(null);

  const dirtyRules  = JSON.stringify(rules)  !== JSON.stringify(savedRules);
  const dirtySystem = JSON.stringify(system) !== JSON.stringify(savedSystem);
  const dirtyTx     = JSON.stringify(txNotify) !== JSON.stringify(savedTx);
  const dirtyGroups = JSON.stringify(groups) !== JSON.stringify(savedGroups);
  const dirtySched  = sched !== savedSched;

  const setTxAt   = (i, next) => setTxNotify(txNotify.map((r, j) => (j === i ? next : r)));
  const removeTx  = (i) => setTxNotify(txNotify.filter((_, j) => j !== i));
  const addTx     = () => setTxNotify([...txNotify, { treatment: treatmentOptions[0] || '', recipients: [{ kind: 'email', value: '' }] }]);

  async function publish(key, value, label) {
    setBusy(true); setError(null); setNotice(null);
    try {
      await onPublish(key, value, { publish: true });
      setNotice(`${label} saved.`);
      setTimeout(() => setNotice(null), 3000);
    } catch (e) {
      setError(e?.message || `Failed to save ${label}.`);
    } finally {
      setBusy(false);
    }
  }

  const updateRule = (i, next) => setRules(rules.map((r, j) => (j === i ? next : r)));
  const deleteRule = (i) => setRules(rules.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rules.length) return;
    const next = [...rules];
    [next[i], next[j]] = [next[j], next[i]];
    setRules(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {error  && <div className="callout danger" style={{ fontSize: 13 }}>{error}</div>}
      {notice && <div className="callout brand" style={{ fontSize: 13 }}>{notice}</div>}

      {/* ── System notifications ── */}
      <section>
        <div className="admin-section-title">System notifications</div>
        <p className="field-hint" style={{ marginTop: 0, marginBottom: 12 }}>
          Built-in flows. Recipients carry the case reference, clinic and a link only — never client or clinical detail.
        </p>

        {SYSTEM_NOTIFICATIONS.map(sys => (
          <div key={sys.id} className="panel" style={{ marginBottom: 10, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text)' }}>{sys.label}</div>
              <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>{TRIGGER_LABELS[sys.trigger] || sys.trigger}</span>
              {sys.editable === 'none' && <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>locked</span>}
            </div>
            <div className="field-hint" style={{ marginTop: 4 }}>{sys.description}</div>

            {sys.editable === 'recipients' && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={system.enabled !== false}
                    onChange={e => setSystem({ ...system, enabled: e.target.checked })}
                    style={{ accentColor: 'var(--brand)', width: 15, height: 15 }} />
                  Send this notification
                </label>
                {system.enabled !== false && (
                  <RecipientEditor specs={system.recipients} onChange={r => setSystem({ ...system, recipients: r })} groups={groups} />
                )}
                <div className="field-hint">Treatment-based extras (e.g. AWI → Injectables lead) are also applied automatically.</div>
                <div>
                  <button type="button" className="btn primary sm" disabled={busy || !dirtySystem}
                    onClick={() => publish('caseSubmittedNotification', system, 'New-case recipients')} style={{ fontSize: 13 }}>
                    {busy ? 'Saving…' : 'Save recipients'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Treatment-based new-case routing — now editable (migrated from a hardcode). */}
        <div className="panel" style={{ marginBottom: 10, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text)' }}>Treatment-based new-case routing</div>
            <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>Case submitted</span>
          </div>
          <div className="field-hint" style={{ marginTop: 4, marginBottom: 10 }}>
            Extra recipients added to the new-case notification, by treatment type (on top of the base recipients above).
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {txNotify.map((row, i) => (
              <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Treatment</span>
                  <select className="input" value={row.treatment || ''} onChange={e => setTxAt(i, { ...row, treatment: e.target.value })}
                    style={{ fontSize: 13, padding: '6px 10px', flex: '0 0 200px' }}>
                    <option value="">Choose…</option>
                    {treatmentOptions.map(v => <option key={v} value={v}>{v}</option>)}
                    {row.treatment && !treatmentOptions.includes(row.treatment) && <option value={row.treatment}>{row.treatment}</option>}
                  </select>
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn ghost sm" onClick={() => removeTx(i)} style={{ fontSize: 12 }}>Remove</button>
                </div>
                <RecipientEditor specs={row.recipients} onChange={r => setTxAt(i, { ...row, recipients: r })} groups={groups} />
              </div>
            ))}
            {txNotify.length === 0 && <div className="field-hint">No treatment-based routing.</div>}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" className="btn ghost sm" onClick={addTx} style={{ fontSize: 13 }}>+ Add treatment</button>
            <button type="button" className="btn primary sm" disabled={busy || !dirtyTx}
              onClick={() => publish('treatmentSubmitNotify', txNotify, 'Treatment routing')} style={{ fontSize: 13 }}>
              {busy ? 'Saving…' : dirtyTx ? 'Save routing' : 'Saved'}
            </button>
          </div>
        </div>
      </section>

      {/* ── Custom flow rules — list + management (editing lives in the wizard) ── */}
      <section>
        <div className="admin-section-title">Custom notification rules</div>
        <p className="field-hint" style={{ marginTop: 0, marginBottom: 12 }}>
          Notify the right people automatically when a case event matches your conditions — or on a daily
          scheduled scan. New rule / Edit opens the rule wizard; enable, order and delete changes here
          are published with Save changes.
        </p>

        {rules.length === 0 && <div className="field-hint" style={{ marginBottom: 12 }}>No custom rules yet.</div>}

        {rules.map((rule, i) => (
          <RuleCard
            key={rule.id || i}
            rule={rule}
            groups={groups}
            onChange={(next) => updateRule(i, next)}
            onDelete={() => deleteRule(i)}
            onEdit={() => navigate(ruleWizardRoute(rule.id))}
            onMoveUp={() => move(i, -1)}
            onMoveDown={() => move(i, +1)}
            isFirst={i === 0}
            isLast={i === rules.length - 1}
          />
        ))}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button type="button" className="btn ghost sm" onClick={() => navigate(RULE_WIZARD_NEW_ROUTE)} style={{ fontSize: 13 }}>+ New rule</button>
          <button type="button" className="btn primary sm" disabled={busy || !dirtyRules}
            onClick={() => publish('notificationRules', rules, 'Notification rules')} style={{ fontSize: 13 }}>
            {busy ? 'Saving…' : dirtyRules ? 'Save changes' : 'Saved'}
          </button>
        </div>

        {/* Scheduled-sending master switch — the daily scan is off until enabled. */}
        <div className="panel" style={{ marginTop: 14, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text)' }}>Scheduled sending</div>
            <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>daily 08:00 UTC</span>
          </div>
          <div className="field-hint" style={{ marginTop: 4, marginBottom: 10 }}>
            Master switch for scheduled rules. When off, scheduled rules stay saved but the daily scan sends nothing.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={sched}
                onChange={e => setSched(e.target.checked)}
                style={{ accentColor: 'var(--brand)', width: 15, height: 15 }} />
              Enable scheduled rules
            </label>
            <button type="button" className="btn primary sm" disabled={busy || !dirtySched}
              onClick={() => publish('scheduledNotificationsEnabled', sched, 'Scheduled sending')} style={{ fontSize: 13 }}>
              {busy ? 'Saving…' : dirtySched ? 'Save switch' : 'Saved'}
            </button>
          </div>
        </div>
      </section>

      {/* ── Recipient groups ── */}
      <section>
        <div className="admin-section-title">Recipient groups</div>
        <p className="field-hint" style={{ marginTop: 0, marginBottom: 12 }}>
          Named staff lists that rules (and the flows above) can target as a single recipient — e.g. “Training team”.
          The key is the stable id rules reference (lowercase letters, digits, - or _). Staff addresses only
          (therapieclinic.com); max 50 per group.
        </p>

        <div className="panel" style={{ padding: '14px 16px' }}>
          <RecipientGroupsEditor groups={groups} onChange={setGroups} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" className="btn primary sm" disabled={busy || !dirtyGroups}
              onClick={() => publish('recipientGroups', groups, 'Recipient groups')} style={{ fontSize: 13 }}>
              {busy ? 'Saving…' : dirtyGroups ? 'Save groups' : 'Saved'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
